import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { SupplierSelect } from "../../components/SupplierSelect";
import { PackageCheck, Search, Package, Truck, CheckCircle2, AlertTriangle, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../../lib/api";
import { fmtDate, fmtDateTime, REPLACEMENT_STATUSES, replacementBadgeClass, waitingColor } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

function ReplBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${replacementBadgeClass(status)}`}>{status}</span>;
}

export default function Replacements() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "WARRANTY_STAFF");
  const [list, setList] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (supplierFilter) params.set("supplier_id", supplierFilter);
    const { data } = await api.get(`/admin/replacements?${params}`);
    setList(data);
  };
  const loadStats = async () => { const { data } = await api.get("/admin/replacements/stats"); setStats(data); };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, statusFilter, supplierFilter]);
  useEffect(() => { loadStats(); }, []);

  const openDetail = async (r) => {
    setDetail({ loading: true });
    const { data } = await api.get(`/admin/replacements/${r.id}`);
    setDetail(data);
  };

  const refreshAll = () => { load(); loadStats(); };

  const KPIS = stats ? [
    { label: "Total Replacements", value: stats.total, icon: Package, color: "text-slate-600 bg-slate-100" },
    { label: "Approved / Waiting", value: stats.approved_waiting, icon: PackageCheck, color: "text-orange-600 bg-orange-50" },
    { label: "Replacement Received", value: stats.received, icon: Truck, color: "text-blue-600 bg-blue-50" },
    { label: "Handed Over", value: stats.handed_over, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
    { label: "Overdue Replacements", value: stats.overdue, icon: AlertTriangle, color: "text-rose-600 bg-rose-50" },
  ] : [];

  return (
    <AdminLayout title="Replacements">
      <div className="mb-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {!stats ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) :
          KPIS.map((c) => (
            <div key={c.label} data-testid={`repl-kpi-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${c.color}`}><c.icon className="h-4 w-4" /></div>
              <div className="font-display text-lg font-bold text-slate-900">{c.value}</div>
              <div className="text-xs text-slate-500">{c.label}</div>
            </div>
          ))}
      </div>

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="repl-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RMA, customer, mobile, product, serial, replacement serial, supplier..." className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["", ...REPLACEMENT_STATUSES].map((s) => (
            <button key={s || "all"} data-testid={`repl-filter-${s ? s.toLowerCase().replace(/[^a-z]+/g, "-") : "all"}`} onClick={() => setStatusFilter(s)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${statusFilter === s ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600"}`}>
              {s || "All Statuses"}
            </button>
          ))}
        </div>
        <div className="w-full lg:w-64"><SupplierSelect value={supplierFilter} onChange={setSupplierFilter} placeholder="All Suppliers" testId="repl-supplier-filter" /></div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>RMA #</TableHead><TableHead>Customer</TableHead><TableHead>WhatsApp</TableHead><TableHead>Supplier</TableHead>
                <TableHead>Original Product</TableHead><TableHead>Original Serial</TableHead><TableHead>Status</TableHead>
                <TableHead>Approved</TableHead><TableHead className="text-center">Waiting</TableHead><TableHead>Received</TableHead>
                <TableHead>Repl. Serial</TableHead><TableHead>Handover</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list === null && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={13}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
              {list?.map((r) => (
                <TableRow key={r.id} data-testid={`repl-row-${r.rma_number}`} className="hover:bg-blue-50/40">
                  <TableCell><button data-testid={`repl-rma-link-${r.rma_number}`} onClick={() => navigate(`/admin/warranty/claims/${r.claim_id}`)} className="font-mono text-sm font-semibold text-blue-700 hover:underline">{r.rma_number}</button></TableCell>
                  <TableCell className="text-sm font-medium text-slate-900">{r.customer_name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{r.mobile || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{r.supplier_name || "—"}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-sm text-slate-700">{r.original_product_name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{r.original_serial_number || "—"}</TableCell>
                  <TableCell><ReplBadge status={r.replacement_status} /></TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(r.approved_date)}</TableCell>
                  <TableCell className="text-center">
                    <span data-testid={`repl-waiting-${r.rma_number}`} className={`text-sm font-bold ${waitingColor(r.waiting_days, r.overdue, stats?.threshold)}`}>
                      {r.waiting_days} {r.waiting_days === 1 ? "Day" : "Days"}{r.overdue ? " ⚠" : ""}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{r.received_date ? fmtDate(r.received_date) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{r.replacement_serial_number || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.handover_date ? fmtDate(r.handover_date) : "—"}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" data-testid={`repl-open-${r.rma_number}`} onClick={() => openDetail(r)}>Open</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {list?.length === 0 && <EmptyState icon={PackageCheck} title="No replacements" description="Replacements appear automatically when a supplier approves a replacement on an RMA." testId="repl-empty" />}
      </div>
      {list && <div className="mt-3 text-sm text-slate-400">{list.length} replacement{list.length !== 1 ? "s" : ""}</div>}

      <ReplacementDetail detail={detail} onClose={() => setDetail(null)} canManage={canManage} onChanged={refreshAll} openDetail={openDetail} navigate={navigate} />
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</div>
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`text-sm font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function ReplacementDetail({ detail, onClose, canManage, onChanged, openDetail, navigate }) {
  const r = detail?.replacement;
  const [statusOpen, setStatusOpen] = useState(false);
  const [edit, setEdit] = useState({ replacement_product_name: "", replacement_serial_number: "", received_date: "", notes: "" });

  useEffect(() => {
    if (r) setEdit({
      replacement_product_name: r.replacement_product_name || "",
      replacement_serial_number: r.replacement_serial_number || "",
      received_date: r.received_date ? r.received_date.slice(0, 10) : "",
      notes: r.notes || "",
    });
  }, [r?.id]);

  const saveEdit = async () => {
    try {
      await api.put(`/admin/replacements/${r.id}`, edit);
      toast.success("Replacement details saved");
      openDetail(r); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/replacements/${r.id}/handover-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Handover proof uploaded"); openDetail(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="repl-detail-dialog">
        <DialogHeader className="sr-only"><DialogTitle>Replacement details</DialogTitle><DialogDescription>View and manage this replacement record and its status.</DialogDescription></DialogHeader>
        {detail?.loading && <Skeleton className="h-64 w-full" />}
        {r && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span className="font-mono">{r.rma_number}</span>
                <ReplBadge status={r.replacement_status} />
              </DialogTitle>
            </DialogHeader>

            <Section title="Warranty Claim">
              <Field label="RMA Number" value={r.rma_number} mono />
              <Field label="Customer" value={r.customer_name} />
              <Field label="WhatsApp" value={r.mobile} mono />
              <div className="flex items-end">
                <button onClick={() => { onClose(); navigate(`/admin/warranty/claims/${r.claim_id}`); }} className="text-xs font-semibold text-blue-700 hover:underline" data-testid="repl-view-rma">Open Warranty Claim →</button>
              </div>
            </Section>

            <Section title="Original Product">
              <Field label="Product Name" value={r.original_product_name} />
              <Field label="Brand" value={r.brand} />
              <Field label="Original Serial" value={r.original_serial_number} mono />
            </Section>

            <Section title="Supplier">
              <Field label="Supplier" value={r.supplier_name} />
              <Field label="Vendor Claim Ref" value={r.vendor_claim_number} mono />
              <Field label="Approved Date" value={fmtDate(r.approved_date)} />
              <Field label="Waiting Days" value={`${r.waiting_days} days${r.overdue ? " (OVERDUE)" : ""}`} />
            </Section>

            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Replacement Product</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="mb-1 block text-xs">Replacement Product Name</Label><Input data-testid="repl-edit-product" value={edit.replacement_product_name} onChange={(e) => setEdit({ ...edit, replacement_product_name: e.target.value })} disabled={!canManage} /></div>
                <div><Label className="mb-1 block text-xs">Replacement Serial Number</Label><Input data-testid="repl-edit-serial" value={edit.replacement_serial_number} onChange={(e) => setEdit({ ...edit, replacement_serial_number: e.target.value })} disabled={!canManage} /></div>
                <div><Label className="mb-1 block text-xs">Received Date</Label><Input type="date" data-testid="repl-edit-received" value={edit.received_date} onChange={(e) => setEdit({ ...edit, received_date: e.target.value })} disabled={!canManage} /></div>
                <div><Label className="mb-1 block text-xs">Notes</Label><Input value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} disabled={!canManage} /></div>
              </div>
              {canManage && <Button size="sm" variant="outline" className="mt-3" data-testid="repl-save-edit" onClick={saveEdit}>Save Details</Button>}
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Customer Handover</div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label="Handover Date" value={r.handover_date ? fmtDate(r.handover_date) : "Pending"} />
                <Field label="Handover By" value={r.handover_employee} />
                <Field label="Handover Note" value={r.handover_note} />
                <div>
                  <div className="text-[11px] text-slate-400">Handover Proof</div>
                  {r.handover_proof_path ? (
                    <a href={fileUrl(r.handover_proof_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"><FileText className="h-3.5 w-3.5" /> View</a>
                  ) : canManage ? (
                    <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-slate-500 hover:text-blue-700">
                      <Upload className="h-3.5 w-3.5" /> Upload
                      <input type="file" className="hidden" data-testid="repl-proof-upload" onChange={(e) => uploadProof(e.target.files[0])} />
                    </label>
                  ) : <span className="text-sm text-slate-400">—</span>}
                </div>
              </div>
            </div>

            {detail.history?.length > 0 && (
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Status History</div>
                <div className="space-y-2">
                  {detail.history.map((h) => (
                    <div key={h.id} className="flex items-start justify-between text-sm">
                      <div><span className="text-slate-500">{h.previous_status}</span> → <span className="font-medium text-slate-900">{h.new_status}</span>{h.note ? <div className="text-xs text-slate-400">{h.note}</div> : null}</div>
                      <div className="text-right text-xs text-slate-400">{h.employee}<br />{fmtDateTime(h.timestamp)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              {canManage && <Button variant="outline" data-testid="print-customer-report-btn" onClick={() => { onClose(); navigate(`/admin/replacements/${r.id}/report`); }}>Print Customer RMA Report</Button>}
              {canManage && <Button data-testid="repl-update-status-btn" className="bg-blue-600 hover:bg-blue-700" onClick={() => setStatusOpen(true)}>Update Status</Button>}
            </DialogFooter>

            <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} r={r} onDone={() => { setStatusOpen(false); openDetail(r); onChanged(); }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ open, onOpenChange, r, onDone }) {
  const [form, setForm] = useState({ status: "", note: "", replacement_serial_number: "", replacement_product_name: "", received_date: "", handover_date: "", handover_note: "" });

  useEffect(() => {
    if (open && r) setForm({
      status: r.replacement_status, note: "",
      replacement_serial_number: r.replacement_serial_number || "",
      replacement_product_name: r.replacement_product_name || "",
      received_date: r.received_date ? r.received_date.slice(0, 10) : "",
      handover_date: r.handover_date ? r.handover_date.slice(0, 10) : "",
      handover_note: "",
    });
  }, [open, r?.id]);

  const submit = async () => {
    if (form.status === "REPLACEMENT RECEIVED" && (!form.replacement_serial_number || !form.received_date)) {
      toast.error("Replacement serial number and received date are required"); return;
    }
    try {
      await api.put(`/admin/replacements/${r.id}/status`, form);
      toast.success("Replacement status updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="repl-status-dialog">
        <DialogHeader><DialogTitle>Update Replacement Status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs">New Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="repl-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REPLACEMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.status === "REPLACEMENT RECEIVED" && (
            <div className="grid gap-3 rounded-lg bg-blue-50 p-3">
              <div><Label className="mb-1 block text-xs">Replacement Serial Number *</Label><Input data-testid="repl-received-serial" value={form.replacement_serial_number} onChange={(e) => setForm({ ...form, replacement_serial_number: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Replacement Product Name</Label><Input value={form.replacement_product_name} onChange={(e) => setForm({ ...form, replacement_product_name: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Received Date *</Label><Input type="date" data-testid="repl-received-date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} /></div>
            </div>
          )}
          {form.status === "REPLACEMENT HANDED OVER TO CUSTOMER" && (
            <div className="grid gap-3 rounded-lg bg-emerald-50 p-3">
              <div><Label className="mb-1 block text-xs">Handover Date</Label><Input type="date" data-testid="repl-handover-date" value={form.handover_date} onChange={(e) => setForm({ ...form, handover_date: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Handover Note (optional)</Label><Textarea rows={2} value={form.handover_note} onChange={(e) => setForm({ ...form, handover_note: e.target.value })} /></div>
            </div>
          )}
          <div><Label className="mb-1 block text-xs">Note (optional)</Label><Textarea data-testid="repl-status-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="repl-status-confirm" className="bg-blue-600 hover:bg-blue-700" onClick={submit}>Update Status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
